/** Only the prepared react-to-print iframe is serialized, never the application document. */
export const MAX_PRINT_HTML_BYTES = 5 * 1024 * 1024

const BROWSER_EXTENSION_STYLESHEET = /^(?:[a-z][a-z\d+.-]*-)?extension:/i

export class PrintSnapshotError extends Error {
  constructor(public readonly code: 'PRINT_PDF_INVALID_HTML' | 'PRINT_PDF_ASSET_FAILED' | 'PRINT_PDF_TOO_LARGE') {
    super(code)
  }
}

function rewriteUrl(value: string, sourceUrl: string, assetBaseUrl: string): string {
  const trimmed = value.trim()
  if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('data:')) return value
  if (/^(?:javascript|vbscript|file):/i.test(trimmed)) return ''
  try {
    const resolved = new URL(trimmed, sourceUrl)
    const source = new URL(sourceUrl)
    if (resolved.origin === source.origin) {
      const destination = new URL(assetBaseUrl)
      return new URL(`${resolved.pathname}${resolved.search}${resolved.hash}`, destination.origin).href
    }
    return resolved.href
  } catch {
    return ''
  }
}

function rewriteCss(css: string, sourceUrl: string, assetBaseUrl: string): string {
  return css
    .replace(/url\(\s*(['"]?)(.*?)\1\s*\)/gi, (_match, _quote: string, value: string) =>
      `url("${rewriteUrl(value, sourceUrl, assetBaseUrl).replace(/"/g, '%22')}")`)
    .replace(/(@import\s+)(['"])(.*?)\2/gi, (_match, prefix: string, quote: string, value: string) =>
      `${prefix}${quote}${rewriteUrl(value, sourceUrl, assetBaseUrl)}${quote}`)
}

/** Follow srcset's whitespace descriptors, retaining commas inside data URLs. */
function rewriteSrcset(value: string, sourceUrl: string, assetBaseUrl: string): string {
  const entries: string[] = []
  let rest = value
  while (rest) {
    rest = rest.replace(/^[\s,]+/, '')
    if (!rest) break
    const url = /^\S+/.exec(rest)?.[0] ?? ''
    rest = rest.slice(url.length)
    if (url.endsWith(',')) {
      entries.push(rewriteUrl(url.replace(/,+$/, ''), sourceUrl, assetBaseUrl))
      continue
    }
    const descriptor = /^[^,]*/.exec(rest)?.[0] ?? ''
    rest = rest.slice(descriptor.length).replace(/^,/, '')
    entries.push(`${rewriteUrl(url, sourceUrl, assetBaseUrl)}${descriptor}`.trim())
  }
  return entries.join(', ')
}

function copyRenderedState(source: Document, target: HTMLElement) {
  const sources = source.querySelectorAll('input,textarea,select,canvas')
  const targets = target.querySelectorAll('input,textarea,select,canvas')
  sources.forEach((element, index) => {
    const clone = targets[index]
    if (!clone) return
    switch (element.tagName.toLowerCase()) {
      case 'input': {
        const input = element as HTMLInputElement
        clone.setAttribute('value', input.value)
        clone.toggleAttribute('checked', input.checked)
        break
      }
      case 'textarea':
        clone.textContent = (element as HTMLTextAreaElement).value
        break
      case 'select': {
        const select = element as HTMLSelectElement
        clone.querySelectorAll('option').forEach((option, optionIndex) => {
          option.toggleAttribute('selected', select.options[optionIndex]?.selected === true)
        })
        break
      }
      case 'canvas': {
        const canvas = element as HTMLCanvasElement
        const image = target.ownerDocument.createElement('img')
        for (const attribute of Array.from(clone.attributes)) image.setAttribute(attribute.name, attribute.value)
        try { image.src = canvas.toDataURL('image/png') } catch { throw new PrintSnapshotError('PRINT_PDF_ASSET_FAILED') }
        clone.replaceWith(image)
        break
      }
    }
  })
}

export function serializePrintDocument(source: Document, assetBaseUrl: string, sourceUrl = window.location.href): string {
  if (!source.body || !source.documentElement) throw new PrintSnapshotError('PRINT_PDF_INVALID_HTML')
  // Detached nodes owned by the live iframe still load new src URLs. An inert document
  // keeps serialization from making duplicate requests to the renderer's asset origin.
  const snapshot = source.implementation.createHTMLDocument('')
  const root = snapshot.importNode(source.documentElement, true) as HTMLElement
  copyRenderedState(source, root)
  root.querySelectorAll('script,noscript,base,iframe,object,embed,applet,audio,video,meta[http-equiv],input[type="hidden"],input[type="password"]').forEach((node) => node.remove())
  root.querySelectorAll('link:not([rel="stylesheet"])').forEach((node) => node.remove())
  // react-to-print also copies styles injected by browser extensions. They are local
  // to the user's browser and cannot be loaded by the server's isolated renderer.
  root.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
    if (BROWSER_EXTENSION_STYLESHEET.test(node.getAttribute('href')?.trim() ?? '')) node.remove()
  })
  // Exclude development ID helpers and other UI explicitly hidden in print media.
  root.querySelectorAll('[class]').forEach((node) => { if (node.classList.contains('print:hidden')) node.remove() })
  for (const element of Array.from(root.querySelectorAll('*'))) {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase()
      if (name.startsWith('on') || ['srcdoc', 'nonce', 'action', 'formaction', 'integrity'].includes(name)) {
        element.removeAttribute(attribute.name)
      } else if (['src', 'href', 'xlink:href', 'poster'].includes(name)) {
        element.setAttribute(attribute.name, rewriteUrl(attribute.value, sourceUrl, assetBaseUrl))
      } else if (name === 'srcset') {
        element.setAttribute(attribute.name, rewriteSrcset(attribute.value, sourceUrl, assetBaseUrl))
      } else if (name === 'style') {
        element.setAttribute(attribute.name, rewriteCss(attribute.value, sourceUrl, assetBaseUrl))
      }
    }
    if (element.tagName.toLowerCase() === 'style') element.textContent = rewriteCss(element.textContent ?? '', sourceUrl, assetBaseUrl)
  }
  const html = `<!DOCTYPE html>\n${root.outerHTML}`
  if (new TextEncoder().encode(html).byteLength > MAX_PRINT_HTML_BYTES) throw new PrintSnapshotError('PRINT_PDF_TOO_LARGE')
  return html
}
